/**
 * Review Queue Page - Scholarly Reflection Processing
 *
 * Split-view interface for evaluating participant reflections.
 * Built with Tarbiyah Heritage aesthetics: scholarly calm, editorial flow.
 */
import { useState } from 'react';
import {
  Search,
  Clock,
  History,
  Loader2,
  BookOpen,
  ShieldCheck,
  Zap,
  Sparkles,
} from 'lucide-react';
import { Button, LoadingSpinner } from '@lms/ui';
import { useReflections, useReviewReflection } from '../hooks/useReflections';
import { useAuthStore } from '../store/auth';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';
// import type { Reflection } from '@lms/shared/types';

export function ReviewQueue() {
  const { user: admin } = useAuthStore();
  const { t } = useLanguage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  const { data: reflections, isLoading } = useReflections({ status: 'PENDING' });
  const reviewMutation = useReviewReflection();

  const items = reflections?.items || [];
  const selectedReflection = items.find(r => r.id === selectedId) || items[0];

  const handleEndorse = () => {
    if (!selectedReflection || !admin) return;
    reviewMutation.mutate(
      {
        id: selectedReflection.id,
        data: {
          status: 'APPROVED',
          feedback: feedback.trim() || t(copy.reviewQueue.defaultEndorseFeedback),
          reviewedBy: admin.id,
        },
      },
      {
        onSuccess: () => {
          setFeedback('');
          setSelectedId(null);
        },
      }
    );
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );

  return (
    <div className="flex h-[calc(100vh-120px)] -m-10 overflow-hidden animate-fadeIn">
      {/* Left Panel: Candidate List (Span 1/4) */}
      <aside
        className={`w-full md:w-80 bg-surface-container-low/50 border-r border-outline-variant/10 flex flex-col ${selectedId !== null ? 'hidden md:flex' : 'flex'}`}
      >
        <div className="p-6 border-b border-outline-variant/10">
          <div className="relative group mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/40 size-4 group-focus-within:text-primary transition-all" />
            <input
              aria-label={t(copy.reviewQueue.searchAriaLabel)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-outline-variant/20 rounded-xl text-sm font-body focus:ring-2 focus:ring-primary/20 transition-all outline-none"
              placeholder={t(copy.reviewQueue.searchPlaceholder)}
              type="text"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
              <Bilingual {...copy.reviewQueue.pendingReview} />
            </span>
            <span className="bg-primary/10 text-primary text-[10px] font-black px-2 py-0.5 rounded-full">
              {reflections?.meta.total || 0}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
          {items.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              className={`w-full text-left p-4 rounded-2xl transition-all border border-transparent group
                ${
                  selectedId === r.id
                    ? 'bg-white shadow-level1 border-primary/20'
                    : !selectedId && items[0]?.id === r.id
                      ? 'md:bg-white md:shadow-level1 md:border-primary/20 hover:bg-white/60 hover:border-outline-variant/20'
                      : 'hover:bg-white/60 hover:border-outline-variant/20'
                }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-surface-container-high flex items-center justify-center text-primary font-bold text-xs">
                  {r.user?.name?.charAt(0)}
                </div>
                <div className="overflow-hidden">
                  <p className="font-headline font-bold text-sm text-on-surface truncate m-0">
                    {r.user?.name}
                  </p>
                  <p className="text-[9px] text-primary font-label font-bold uppercase tracking-tighter m-0">
                    <Bilingual {...copy.reviewQueue.submittedAgo} />
                  </p>
                </div>
              </div>
              <p className="text-[11px] font-body italic text-on-surface-variant leading-tight line-clamp-2">
                {r.material?.title || t(copy.reviewQueue.courseReflection)}
              </p>
            </button>
          ))}
          {items.length === 0 && (
            <div className="p-8 text-center bg-surface-container-low/30 rounded-2xl italic text-on-surface-variant text-xs">
              <Bilingual {...copy.reviewQueue.queueCleared} />
            </div>
          )}
        </div>
      </aside>

      {/* Right Panel: Scholarly Workspace (Span 3/4) */}
      <main
        className={`flex-1 bg-surface overflow-y-auto relative custom-scrollbar ${selectedId === null ? 'hidden md:block' : 'block'}`}
      >
        {selectedReflection ? (
          <div className="max-w-3xl mx-auto p-6 py-8 md:p-12 md:py-16 space-y-14">
            {/* Mobile Back Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                onClick={() => setSelectedId(null)}
                className="text-[10px] uppercase font-bold tracking-widest flex items-center gap-2 pl-0 mb-6"
              >
                &larr; <Bilingual {...copy.reviewQueue.btnBackToList} />
              </Button>
            </div>
            {/* Context Prompt */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[10px] font-label font-black text-secondary uppercase tracking-[0.25em]">
                  <Bilingual {...copy.reviewQueue.inquiryPrompt} />
                </span>
                <div className="h-px flex-1 bg-secondary/10" />
              </div>
              <div className="bg-surface-container-low/40 p-10 rounded-3xl border border-outline-variant/10 relative overflow-hidden">
                <BookOpen
                  size={80}
                  className="absolute -right-4 -bottom-4 text-primary opacity-[0.03] pointer-events-none"
                />
                <h2 className="text-2xl font-headline font-bold text-on-surface mb-8 leading-tight italic m-0">
                  "{selectedReflection.prompt}"
                </h2>
                <div className="flex items-center gap-6 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60">
                  <span className="flex items-center gap-2">
                    <Clock size={12} /> {selectedReflection.material?.title}
                  </span>
                  <span className="flex items-center gap-2">
                    <Zap size={12} /> <Bilingual {...copy.reviewQueue.analyticalReflection} />
                  </span>
                </div>
              </div>
            </section>

            {/* Participant Response */}
            <section>
              <div className="flex items-center justify-between mb-10 border-b border-outline-variant/10 pb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-surface-container-high border-2 border-primary/10 flex items-center justify-center text-primary font-headline font-bold text-xl">
                    {selectedReflection.user?.name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-headline font-bold text-2xl text-on-surface m-0">
                      {selectedReflection.user?.name}
                    </h3>
                    <p className="text-xs font-label text-on-surface-variant uppercase tracking-widest font-bold opacity-60">
                      <Bilingual {...copy.reviewQueue.scholarCandidate} />
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-[0.2em] mb-1">
                    <Bilingual {...copy.reviewQueue.lexicalVolume} />
                  </p>
                  <p className="text-3xl font-headline font-black text-primary m-0">
                    {selectedReflection.response?.split(' ').length || 0}
                  </p>
                </div>
              </div>

              <div className="prose prose-stone max-w-none">
                <p className="text-xl font-body leading-relaxed text-on-surface whitespace-pre-wrap">
                  {selectedReflection.response}
                </p>
              </div>
            </section>

            {/* Feedback & Review Form */}
            <section className="pt-10">
              <div className="bg-primary/5 p-8 rounded-[2rem] border border-primary/10 shadow-level1 relative overflow-hidden group">
                <Sparkles
                  size={120}
                  className="absolute -right-8 -bottom-8 text-primary opacity-[0.02] pointer-events-none group-hover:scale-110 transition-transform duration-1000"
                />
                <label className="block text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] mb-6 pl-2">
                  <Bilingual {...copy.reviewQueue.instructorFeedback} />
                </label>
                <textarea
                  className="w-full bg-white border border-outline-variant/30 rounded-2xl p-6 text-lg font-body focus:ring-2 focus:ring-primary/20 transition-all outline-none placeholder:text-on-surface-variant/30 mb-8 min-h-[200px]"
                  placeholder={t(copy.reviewQueue.feedbackPlaceholder)}
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      className="rounded-xl px-6 text-[10px] uppercase font-bold tracking-widest"
                    >
                      <Bilingual {...copy.reviewQueue.btnRequestRevision} />
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-[10px] uppercase font-bold tracking-widest opacity-60"
                    >
                      <Bilingual {...copy.reviewQueue.btnArchiveDraft} />
                    </Button>
                  </div>
                  <Button
                    onClick={handleEndorse}
                    disabled={reviewMutation.isPending}
                    className="rounded-xl px-10 text-[10px] uppercase font-bold tracking-[0.2em] shadow-level2"
                  >
                    {reviewMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Bilingual {...copy.reviewQueue.btnEndorseReflection} />
                    )}
                  </Button>
                </div>
              </div>
            </section>

            {/* History Footnote */}
            <footer className="pt-16 border-t border-outline-variant/10 opacity-30">
              <h4 className="text-[10px] font-label font-black text-secondary uppercase tracking-[0.25em] mb-6">
                <Bilingual {...copy.reviewQueue.historicalPedagogy} />
              </h4>
              <div className="flex items-start gap-4">
                <History size={16} className="text-on-surface-variant" />
                <p className="text-xs font-body italic text-on-surface-variant leading-relaxed m-0">
                  <Bilingual {...copy.reviewQueue.historicalNote} />
                </p>
              </div>
            </footer>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center p-12 text-center space-y-6">
            <div className="w-24 h-24 bg-surface-container-low rounded-full flex items-center justify-center text-outline-variant/20">
              <ShieldCheck size={48} />
            </div>
            <div>
              <h2 className="text-xl font-headline font-bold text-on-surface m-0">
                <Bilingual {...copy.reviewQueue.selectAssignment} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-2 max-w-xs">
                <Bilingual {...copy.reviewQueue.selectAssignmentDesc} />
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
